import { FC, JSX, useMemo } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchAllProperties,
  fetchAllPropertiesEarly,
  fetchCachedGeometries,
  fetchLookups,
} from '@api';
import { PropertiesContext, PropertiesModel } from './PropertiesContext.ts';
import { Property, Geometry } from '@model';

const processGeometries = (
  cachedGeometries: Geometry[],
  propertyAreas: string[],
  culture: string,
) => {
  const processedGeometries = cachedGeometries
    .map(
      (geom) =>
        ({
          ...geom,
          alt_name:
            culture === 'el' ? geom.alt_name : geom.tags[`alt_name:${culture}`],
          name: culture === 'el' ? geom.name : geom.tags[`name:${culture}`],
        }) as Geometry,
    )
    .filter((geom) => geom.name);

  // Scan missing areas and fill from geometries api
  const missingGeometries = [];
  for (const area of propertyAreas) {
    const geom = processedGeometries.find(
      (geom) =>
        geom.name.toLowerCase() === area.toLowerCase() ||
        geom.alt_name?.toLowerCase() === area.toLowerCase(),
    );
    if (!geom) {
      missingGeometries.push(area);
    }
  }

  // if (!!missingGeometries.length) {
  //   console.warn('Missing geometries:', missingGeometries);
  //   const refreshedGeometries = await fetchMissingGeometries(missingGeometries, culture);
  //   console.info('Fetched new geometries:', refreshedGeometries);
  //   processedGeometries.push(...Object.values(refreshedGeometries));
  // }

  return processedGeometries;
};

const processMeta = (fetchedProperties: Property[]) => {
  return {
    propertyAreas: [...new Set(fetchedProperties.map((p) => p.area))].sort(
      (a, b) => a.localeCompare(b),
    ),
  };
};

const noProperties: Property[] = [];
const noGeometries: Geometry[] = [];

export const PropertiesProvider: FC<{ children: JSX.Element }> = (props) => {
  // i18next owns the current language; this re-renders when it changes
  const { i18n } = useTranslation();
  const culture = i18n.resolvedLanguage ?? i18n.language;
  const queryClient = useQueryClient();

  // Queries are keyed by culture, so responses for a previous culture never
  // overwrite the current one
  const lookupsQuery = useQuery({
    queryKey: ['lookups', culture],
    queryFn: () => fetchLookups(culture),
    placeholderData: keepPreviousData,
  });

  const propertiesEarlyQuery = useQuery({
    queryKey: ['properties', culture, 'early'],
    queryFn: () => fetchAllPropertiesEarly(culture),
    placeholderData: keepPreviousData,
  });

  const propertiesQuery = useQuery({
    queryKey: ['properties', culture],
    queryFn: () => fetchAllProperties(culture),
  });

  const geometriesQuery = useQuery({
    queryKey: ['geometries'],
    queryFn: fetchCachedGeometries,
    staleTime: Infinity,
  });

  // The full list wins once it arrives; the static blob fills in until then
  const properties =
    propertiesQuery.data ?? propertiesEarlyQuery.data ?? noProperties;

  const { propertyAreas } = useMemo(
    () => processMeta(properties),
    [properties],
  );

  const geometries = useMemo(
    () =>
      geometriesQuery.data
        ? processGeometries(geometriesQuery.data, propertyAreas, culture)
        : noGeometries,
    [geometriesQuery.data, propertyAreas, culture],
  );

  const model = {
    allAreasWithProperties: propertyAreas,
    culture,
    setCulture: (culture: string) => void i18n.changeLanguage(culture),
    geometries,
    lookups: lookupsQuery.data,
    properties,
    refresh: () => queryClient.invalidateQueries({ queryKey: ['properties'] }),
  } as PropertiesModel;

  return (
    <PropertiesContext.Provider value={model}>
      {props.children}
    </PropertiesContext.Provider>
  );
};
